import React from 'react';
import { format } from 'date-fns';
import logoSrc from '../../assets/img/fit2ride-bl-logo.png';

const PrintablePackingSlip = ({ packingSlip }) => {
    if (!packingSlip) {
        return null;
    }

    const ITEMS_PER_PAGE = 14;
    const LAST_PAGE_MAX = 7; // Max items on last page to leave room for shipping info
    const items = packingSlip.items || [];
    const chunks = [];

    if (items.length === 0) {
        // No items, create one empty chunk
        chunks.push([]);
    } else if (items.length <= LAST_PAGE_MAX) {
        // All items fit on one page with room for shipping info
        chunks.push(items);
    } else {
        // Multiple pages needed
        // Strategy: Fill pages with 14 items until we have ≤ ITEMS_PER_PAGE + LAST_PAGE_MAX left
        // Then maximize second-to-last page, minimize last page

        let currentIndex = 0;

        // Fill pages with 14 items while more than (14 + 9) items remain
        while (items.length - currentIndex > ITEMS_PER_PAGE + LAST_PAGE_MAX) {
            chunks.push(items.slice(currentIndex, currentIndex + ITEMS_PER_PAGE));
            currentIndex += ITEMS_PER_PAGE;
        }

        // Now we have ≤ 23 items remaining
        const remaining = items.length - currentIndex;

        if (remaining <= LAST_PAGE_MAX) {
            // All remaining items fit on last page
            chunks.push(items.slice(currentIndex));
        } else {
            // Split remaining across 2 pages
            // Maximize second-to-last page (up to ITEMS_PER_PAGE), minimize last page
            const secondToLastCount = Math.min(remaining - 1, ITEMS_PER_PAGE);
            chunks.push(items.slice(currentIndex, currentIndex + secondToLastCount));
            chunks.push(items.slice(currentIndex + secondToLastCount));
        }
    }

    return (
        <>
            <style>{`
                /* Print-specific styles */
                @media print {
                    main {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .main-layout-bg {
                        background: #fff !important;
                    }

                    /* Hide all navigation, headers, and non-printable elements */
                    .no-print,
                    header,
                    nav,
                    aside,
                    [class*="sidebar"],
                    [class*="Sidebar"],
                    [class*="Header"],
                    [class*="navigation"],
                    [role="navigation"],
                    button:not(.print-only button),
                    .Toastify {
                        display: none !important;
                    }

                    /* Page setup */
                    @page {
                        size: letter;
                        margin: 0.5in;
                    }

                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        height: auto !important;
                        overflow: visible !important;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }

                    /* Remove all scrolling and height constraints from all elements */
                    * {
                        overflow: visible !important;
                        max-height: none !important;
                    }

                    /* Critical: Override MainLayout constraints that cause scroll-based printing */
                    /* MainLayout has h-screen and overflow-auto that must be removed */
                    .flex.h-screen,
                    main.overflow-auto,
                    main {
                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;
                        overflow: visible !important;
                    }

                    /* Force all flex containers to allow natural height */
                    .flex,
                    .flex-1,
                    .flex-col {
                        height: auto !important;
                        min-height: 0 !important;
                    }

                    /* Target React root and all parent divs */
                    #root,
                    #root > div,
                    body > div {
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                    }

                    /* Each print page */
                    .print-page {
                        display: block;
                        position: relative;
                        width: 100%;
                        margin: 0;
                        padding: 0;
                        page-break-after: always;
                    }

                    .print-page:last-child {
                        page-break-after: auto;
                    }

                    /* Keep customer info and shipping sections together */
                    .customer-info-box,
                    .shipping-info-section {
                        page-break-inside: avoid;
                    }

                    .page-header {
                        display: block;
                        width: 100%;
                        margin-bottom: 1rem;
                    }

                    .page-footer {
                        display: block;
                        width: 100%;
                        margin-top: 1rem;
                    }

                    /* Show print-only elements */
                    .print-only {
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    /* Ensure first print page starts at top */
                    .print-only > .print-page:first-child {
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                    }

                    /* Container resets */
                    .printable-packing-slip-container {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }

                    /* Product table styles */
                    .printable-packing-slip-container table {
                        width: 100%;
                        border-collapse: collapse;
                        page-break-inside: auto;
                        margin: 0;
                    }

                    .printable-packing-slip-container table,
                    .printable-packing-slip-container th,
                    .printable-packing-slip-container td {
                        border: 1px solid #d1d5db;
                    }

                    .printable-packing-slip-container td.border-none {
                        border: none !important;
                    }

                    .printable-packing-slip-container th,
                    .printable-packing-slip-container td {
                        padding: 8px;
                        text-align: left;
                    }

                    .printable-packing-slip-container thead {
                        display: table-header-group;
                    }

                    /* Other element styles */
                    .bg-gray-100 {
                        background-color: #fff !important;
                        padding: 1rem;
                    }

                    .border-b {
                        border-bottom: 1px solid #9ca3af;
                    }

                    .border-t {
                        border-top: 1px solid #e5e7eb;
                    }

                    .mb-8 {
                        margin-bottom: 1.5rem;
                    }

                    .mb-4 {
                        margin-bottom: 0.75rem;
                    }

                    .grid {
                        display: grid;
                        page-break-inside: auto;
                    }

                    .grid-cols-2 {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .gap-8 {
                        gap: 2rem;
                    }

                    /* Reduce section margins to allow more content per page */
                    .mb-8 {
                        margin-bottom: 1rem !important;
                    }

                    .mb-4 {
                        margin-bottom: 0.5rem !important;
                    }
                }
            `}</style>

            <div className="printable-packing-slip-container">
                {chunks.map((chunk, chunkIndex) => (
                    <div key={chunkIndex} className="print-page">
                        {/* Page Header */}
                        <div className="page-header">
                            <div className="flex items-center justify-between mb-4">
                                <img src={logoSrc} alt="Fit2Ride Logo" style={{ height: '50px', width: '50px' }} />
                                <div className="text-center flex-1">
                                    <div style={{ fontSize: '18pt', fontWeight: 'bold' }}>FIT PROTECTION</div>
                                    <div style={{ fontSize: '10pt', color: '#666' }}>PACKING SLIP</div>
                                </div>
                                <div style={{ width: '50px' }}></div>
                            </div>
                        </div>

                        {/* Customer info */}
                        <div className="customer-info-box mb-4">
                            <div className="bg-gray-100 p-4">
                                <h3 className="font-bold text-gray-800 mb-2">Customer:</h3>
                                <div className="flex flex-row items-center justify-between">
                                    <div className="space-y-1">
                                        <div>Company: VIDA Distribution (3PL)</div>
                                        <div>9245 S Farmer Ave STE. 110, Tempe, AZ 85284</div>
                                    </div>
                                    <div>
                                        <div>Phone: (480) 247-2337</div>
                                        <div>Email: receiving@vidadistro.com</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Products table */}
                        <div className="mb-4">
                            <table className="min-w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Product Description</th>
                                        <th className="border border-gray-300 px-4 py-2 text-left font-semibold">SKU</th>
                                        <th className="border border-gray-300 px-4 py-2 text-center font-semibold">Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chunk.map((item, itemIndex) => (
                                        <tr key={itemIndex}>
                                            <td className="border border-gray-300 px-4 py-3">{item.product_description}</td>
                                            <td className="border border-gray-300 px-4 py-3">{item.product_sku}</td>
                                            <td className="border border-gray-300 px-4 py-3 text-center">{item.quantity}</td>
                                        </tr>
                                    ))}
                                    {/* Add blank rows to fill the last page - fill to 9 rows total */}
                                    {(chunkIndex === chunks.length - 1) &&
                                        Array.from({ length: Math.max(0, LAST_PAGE_MAX - chunk.length) }).map((_, index) => (
                                            <tr key={`empty-${index}`}>
                                                <td className="border border-none px-4 py-3">&nbsp;</td>
                                                <td className="border border-none px-4 py-3">&nbsp;</td>
                                                <td className="border border-none px-4 py-3">&nbsp;</td>
                                            </tr>
                                        ))
                                    }

                                    {(chunkIndex !== chunks.length - 1 && chunkIndex < chunks.length - 1) &&
                                        Array.from({ length: Math.max(0, ITEMS_PER_PAGE - chunk.length) }).map((_, index) => (
                                            <tr key={`empty-${index}`}>
                                                <td className="border border-none px-4 py-3">&nbsp;</td>
                                                <td className="border border-none  px-4 py-3">&nbsp;</td>
                                                <td className="border border-none  px-4 py-3">&nbsp;</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Only show shipping info and special instructions on last page */}
                        {chunkIndex === chunks.length - 1 && (
                            <div className="shipping-info-section">
                                {/* Shipping & Receiving Info */}
                                <div className="grid grid-cols-2 gap-8 mb-4">
                                    <div>
                                        <div className="mb-4">
                                            <div className="font-semibold mb-2">Date Packed:</div>
                                            <div className={`border-b border-gray-400 pb-1 ${packingSlip.date_packed ? '':'pt-6'}`}>
                                                {packingSlip.date_packed ? format(new Date(packingSlip.date_packed), 'MM/dd/yyyy') : ''}
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <div className="font-semibold mb-2">Date Shipped:</div>
                                            <div className={`border-b border-gray-400 pb-1 ${packingSlip.date_shipped ? '':'pt-6'}`}>
                                                {packingSlip.date_shipped ? format(new Date(packingSlip.date_shipped), 'MM/dd/yyyy') : ''}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-semibold mb-2">Packed By:</div>
                                            <div className={`border-b border-gray-400 pb-1 ${packingSlip.packed_by ? '':'pt-6'}`}>
                                                {packingSlip.packed_by || ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="mb-4">
                                            <div className="font-semibold mb-2">Date Received:</div>
                                            <div className={`border-b border-gray-400 pb-1 ${packingSlip.date_received ? '':'pt-6'}`}>
                                                {packingSlip.date_received ? format(new Date(packingSlip.date_received), 'MM/dd/yyyy') : ''}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-semibold mb-2">Received By:</div>
                                            <div className={`border-b border-gray-400 pb-1 ${packingSlip.date_received_by ? '':'pt-6'}`}>
                                                {packingSlip.received_by || ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Special Instructions */}
                                <div className="mb-4">
                                    <div className="font-semibold mb-2">Special Instructions / Comments</div>
                                    <div className="border-b border-gray-400 pb-1 min-h-[30px]">
                                        {packingSlip.special_instructions || ''}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Page Footer */}
                        <div className="page-footer">
                            <div className="border-t pt-2 flex justify-between" style={{ fontSize: '8pt', color: '#666' }}>
                                <div>
                                    <div>Packing Slip #: {packingSlip.packing_slip_number}</div>
                                    <div>Production Batch #: {packingSlip.production_batch?.batch_number}</div>
                                    <div>Created: {format(new Date(packingSlip.created_at), 'MM/dd/yyyy')}</div>
                                    <div style={{ marginTop: '4px', fontWeight: '600' }}>Page {chunkIndex + 1} of {chunks.length}</div>
                                </div>
                                <div className="text-right">
                                    <div style={{ fontWeight: '600' }}>FIT PROTECTION</div>
                                    <div>3215 E WASHINGTON ST</div>
                                    <div>PHOENIX, AZ 85395</div>
                                    <div>INFO@FITPROTECTION.COM</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default PrintablePackingSlip;
